import { FileItem, Item, type Location as FileLocation } from "@/types/fse";
import { FlattenedItem } from "@/modules/chat/chat-ui.types";

export const flatFileSystem = (
  item: Item,
  depth: number = 0,
  expandedFolders: Set<string> | undefined,
  showFolders: boolean,
): FlattenedItem[] => {
  if (item.type === "file") {
    return [{ ...item, depth, isExpanded: false }];
  }

  const isExpanded = expandedFolders ? expandedFolders.has(item.id) : true;
  const result: FlattenedItem[] = showFolders ? [{ ...item, depth, isExpanded }] : [];

  if (isExpanded || !showFolders) {
    const sortedItems = Object.values(item.items).sort((a, b) => {
      if (a.type === "folder" && b.type === "file") return -1;
      if (a.type === "file" && b.type === "folder") return 1;
      return 0;
    });
    sortedItems.forEach((subItem: Item) => {
      result.push(...flatFileSystem(subItem, showFolders ? depth + 1 : depth, expandedFolders, showFolders));
    });
  }

  return result;
};

export const getParentLocations = (item: FileItem): FileLocation[] =>
  item.locations.map(location => ({
    id: location.path[location.path.length - 2]?.id || location.id,
    path: location.path.slice(0, -1),
  }));

/** Recursively collects the file IDs within a folder; `skipRejected` leaves out files that failed ingestion. */
export const getFilesInFolder = (folderId: string, rootData: Item, { skipRejected }: { skipRejected: boolean }) => {
  const findFolder = (item: Item): Item | null => {
    if (item.id === folderId) return item;
    if (item.type === "folder") {
      for (const childItem of Object.values(item.items)) {
        const found = findFolder(childItem);
        if (found) return found;
      }
    }
    return null;
  };

  const collectFileIds = (item: Item): string[] => {
    if (item.type === "file") {
      return skipRejected && item.rejection_reason ? [] : [item.id];
    }
    const fileIds: string[] = [];
    Object.values(item.items).forEach(childItem => {
      fileIds.push(...collectFileIds(childItem));
    });
    return fileIds;
  };

  const folder = findFolder(rootData);
  return folder ? collectFileIds(folder) : [];
};
